import React, { useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * Skip-to-content link.
 *
 * The first thing a keyboard or screen-reader user meets on a page, so they can
 * jump the header, the announcement banner, and the hero animation instead of
 * tabbing through all of it. Visually hidden until it takes focus.
 *
 * Rendered once from SiteHeader, so every marketing page gets it rather than
 * each page hand-rolling its own.
 *
 * The click handler resolves the target at runtime instead of relying on the
 * href fragment alone. Pages label their content landmark differently (some
 * carry #main-content, some only a <main>, some neither yet), and a skip link
 * that lands nowhere is worse than none. Resolution order: #main-content, then
 * the first <main>, then the first h1. It also moves real focus to the target,
 * which a bare fragment jump does not do in every browser.
 */
export default function SkipLink({ targetId = 'main-content' }) {
  const handleClick = useCallback((event) => {
    if (typeof document === 'undefined') return;
    const target =
      document.getElementById(targetId) ||
      document.querySelector('main') ||
      document.querySelector('h1');
    if (!target) return; // leave the default fragment behaviour alone
    event.preventDefault();
    // tabIndex -1 lets a non-interactive landmark accept programmatic focus
    // without entering the tab order.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'start' });
  }, [targetId]);

  return (
    <Box
      component="a"
      href={`#${targetId}`}
      onClick={handleClick}
      sx={{
        position: 'fixed',
        left: 16,
        top: -80,
        zIndex: 2200,
        px: 2,
        py: 1,
        borderRadius: '8px',
        backgroundColor: '#B83B1D',
        color: '#FFFFFF',
        fontSize: '0.85rem',
        fontWeight: 600,
        textDecoration: 'none',
        boxShadow: '0 6px 18px rgba(46, 20, 10, 0.22)',
        transition: 'top 0.2s ease',
        '&:focus': { top: 16 },
        '&:focus-visible': { top: 16, outline: '2px solid #FFFFFF', outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      Skip to content
    </Box>
  );
}
